import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, PhoneCall, CalendarCheck } from "lucide-react";
import { CLINIC, SERVICES } from "@/data/content";

const NAV_LINKS = [
  { to: "/about-dr-ankur-lal", label: "Dr. Ankur Lal" },
  { to: "/std-sti-treatment", label: "STD/STI Treatment" },
  { to: "/hiv-pep-delhi", label: "HIV PEP" },
  { to: "/std-symptoms", label: "Symptoms" },
  { to: "/std-hiv-testing", label: "Testing" },
  { to: "/online-consultation", label: "Online Consultation" },
  { to: "/privacy-confidentiality", label: "Privacy" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[#FAF9F6]/90 backdrop-blur-xl shadow-[0_1px_0_rgba(15,23,42,0.07)]"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <img
              src="/images/logo-mark.png"
              alt="Delhi STD Clinic logo"
              className="h-10 sm:h-11 w-auto"
              data-testid="nav-logo-image"
            />
            <span className="flex flex-col leading-none">
              <span className="font-serif text-xl sm:text-2xl font-semibold text-slate-950 tracking-tight">
                Delhi STD Clinic
              </span>
              <span className="text-[10px] sm:text-[11px] tracking-[0.22em] uppercase text-emerald-900/75 font-semibold mt-1">
                by Dr. Ankur Lal
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7" data-testid="nav-desktop">
            {NAV_LINKS.slice(0, 5).map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[13px] font-medium tracking-wide transition-colors ${
                    isActive ? "text-emerald-900" : "text-slate-600 hover:text-slate-950"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={`tel:${CLINIC.phoneRaw}`}
              data-testid="nav-call-button"
              className="hidden md:inline-flex items-center gap-2 text-[13px] font-semibold text-emerald-950 hover:text-emerald-700 transition-colors"
            >
              <PhoneCall className="w-4 h-4" />
              {CLINIC.phoneDisplay}
            </a>
            <Link
              to="/booking?type=online"
              data-testid="nav-book-button"
              className="hidden sm:inline-flex items-center gap-2 bg-emerald-900 hover:bg-emerald-800 text-white text-[13px] font-semibold px-5 py-2.5 rounded-full transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
            >
              <CalendarCheck className="w-4 h-4" />
              Book Online Consultation
            </Link>
            <button
              onClick={() => setOpen(!open)}
              data-testid="nav-menu-toggle"
              aria-label="Toggle menu"
              className="inline-flex lg:hidden items-center justify-center w-10 h-10 rounded-full border border-slate-300 text-slate-800"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-slate-950/97 backdrop-blur-xl pt-24 pb-28 px-7 overflow-y-auto"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1">
              {[{ to: "/", label: "Home" }, ...NAV_LINKS, { to: "/booking", label: "Book Appointment" }, { to: "/privacy-policy", label: "Privacy & Legal" }].map(
                (l, i) => (
                  <motion.div
                    key={l.to + l.label}
                    initial={{ opacity: 0, x: -18 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 + i * 0.04 }}
                  >
                    <NavLink
                      to={l.to}
                      data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="block py-3 border-b border-white/8 font-serif text-2xl text-slate-100 hover:text-amber-300 transition-colors"
                    >
                      {l.label}
                    </NavLink>
                  </motion.div>
                )
              )}
              <p className="mt-6 text-xs tracking-[0.2em] uppercase text-emerald-300/80">
                Treatments
              </p>
              <div className="grid grid-cols-2 gap-x-4">
                {SERVICES.map((s) => (
                  <NavLink
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    data-testid={`mobile-nav-service-${s.slug}`}
                    className="py-2 text-sm text-slate-400 hover:text-amber-300 transition-colors"
                  >
                    {s.name}
                  </NavLink>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
